import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import ScheduledMessageActions from '../scheduled-messages/ScheduledMessageActions';
import ScheduledMessageDialogs from '../scheduled-messages/ScheduledMessageDialogs';
import { DetailsSectionHeader } from './DetailsSection';
import useScheduledMessages, {
  formatScheduledDate,
} from '../scheduled-messages/useScheduledMessages';

const getStoredOpenState = () => {
  try {
    const raw = window.localStorage.getItem(`conversation-details:scheduled`);

    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
};

const ScheduledMessageInfo = ({ customer, refreshKey, onChanged, onResolved }) => {
  const [isOpen, setIsOpen] = useState(getStoredOpenState);

  const customerId = customer?._id;

  const {
    loading,
    error,
    scheduledMessages,
    activeActionId,
    sendNow,
    deleteScheduledMessage,
    dialogs,
  } = useScheduledMessages({
    customerId,
    refreshKey,
    enabled: isOpen,
    onChanged,
    onResolved,
  });

  const handleOpenChange = (value) => {
    try {
      window.localStorage.setItem(`conversation-details:scheduled`, String(value));
    } catch {
      // Ignore storage failures; the section can still be used.
    }

    setIsOpen(value);
  };

  const renderEmptyState = ({ isLoading = false } = {}) => (
    <div className="relative rounded-lg border border-dashed border-border bg-muted/20 p-3">
      {/* The loader reuses the empty state's layout so it takes the same space */}
      <div className={isLoading ? 'invisible' : undefined} aria-hidden={isLoading}>
        <p className="text-sm font-medium text-foreground">
          No scheduled messages for this customer.
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          When you schedule a future message, it will show up here so you can review or change it
          before it sends.
        </p>
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="size-4 animate-spin text-primary" aria-label="Loading scheduled messages" />
        </div>
      )}
    </div>
  );

  const renderScheduledMessages = () => {
    // refetches keep showing the current list instead of swapping in the loader
    if (loading && scheduledMessages.length === 0) {
      return renderEmptyState({ isLoading: true });
    }

    if (error) {
      return (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <p className="text-sm font-medium text-foreground">
            Scheduled messages could not be loaded.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Please try refreshing this conversation.
          </p>
        </div>
      );
    }

    if (scheduledMessages.length === 0) {
      return renderEmptyState();
    }

    return scheduledMessages.map((scheduledMessage) => {
      const isActionLoading = activeActionId === scheduledMessage._id;

      return (
        <div key={scheduledMessage._id} className="rounded-lg bg-muted/30">
          <div className="space-y-1.5 px-3 pt-3 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {scheduledMessage.textingBlocked ? 'Sending stopped — texting was unavailable' : `Sending ${formatScheduledDate(scheduledMessage.scheduledSendDate)}`}
            </p>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
              {scheduledMessage.text}
            </p>
            {scheduledMessage.textingBlocked && (
              <p className="text-sm leading-relaxed text-muted-foreground">Nothing else will send automatically. Once texting is ready, review the conversation before writing a new message.</p>
            )}
          </div>

          <ScheduledMessageActions
            scheduledMessage={scheduledMessage}
            dialogs={dialogs}
            onSendNow={sendNow}
            onDelete={deleteScheduledMessage}
            isActionLoading={isActionLoading}
          />
        </div>
      );
    });
  };

  return (
    <div className="border-b border-border/60">
      <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
        <DetailsSectionHeader title="Scheduled messages" />
        <CollapsibleContent>
          <div className="px-4 pb-3 space-y-2">{renderScheduledMessages()}</div>
        </CollapsibleContent>
      </Collapsible>

      <ScheduledMessageDialogs dialogs={dialogs} />
    </div>
  );
};

export default ScheduledMessageInfo;
